import { useCallback } from 'react';
import dayjs from 'dayjs';
import { TIME_CONSTANTS } from '../constants/slotConstants';

/**
 * Custom hook for slot time calculation logic
 */
export const useEventCalendarTimeLogic = (selectedDate, existingSlots, viewMode, constraints = {}) => {
  // Calculate default time - use clicked time only for week view, smart default for month view
  const getDefaultTime = useCallback(() => {
    const clickedDate = dayjs(selectedDate);
    const minFutureTime = dayjs().add(TIME_CONSTANTS.MIN_FUTURE_HOURS, 'hour');

    const { allowedTimeStart, allowedTimeEnd, durationMinutes } = constraints || {};
    const duration = Number(durationMinutes) || 0;

    // Convert a HH:mm that is expressed in UTC into a local Dayjs for the given selectedDate
    // The backend stores start/end times in UTC (e.g. 13:00 means 1:00 PM UTC).
    // We need to convert this to the user's local timezone for validation and display.
    // Example: If user is in CET (UTC+1), 13:00 UTC becomes 14:00 local time.
    const utcHHMMToLocalDate = (hhmm) => {
      if (!hhmm) return null;
      const [h, m] = String(hhmm)
        .split(':')
        .map((n) => Number(n));
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      const y = clickedDate.year();
      const mo = clickedDate.month();
      const d = clickedDate.date();
      // Date.UTC creates a timestamp assuming the given components are UTC.
      // new Date(utcMillis) then creates a Date object that wraps that timestamp,
      // which methods like .getHours() or dayjs() will read in local time.
      const utcMillis = Date.UTC(y, mo, d, h, m, 0, 0);
      return dayjs(new Date(utcMillis));
    };

    // For month view, always use smart default logic (never use clicked time)
    // For week view, use clicked time if it has specific time information (not midnight)
    if (viewMode === 'week' && (clickedDate.hour() !== 0 || clickedDate.minute() !== 0)) {
      const proposedTime = clickedDate.format('HH:mm');
      const proposedDateTime = dayjs(selectedDate)
        .hour(clickedDate.hour())
        .minute(clickedDate.minute())
        .second(0)
        .millisecond(0);

      // Only use clicked time if it's at least 1 hour in the future
      if (proposedDateTime.isAfter(minFutureTime)) {
        return proposedTime;
      }
      // Otherwise fall through to smart default logic
    }

    // Smart default logic for month view clicks or week view midnight clicks
    const sameDay = clickedDate.format('YYYY-MM-DD');
    const slotsOnSameDay = existingSlots.filter((slot) => dayjs(slot.startTime).format('YYYY-MM-DD') === sameDay);

    let suggestedTime;

    // If tenant defines allowed bounds, prefer the earliest free+valid time within bounds
    if (allowedTimeStart || allowedTimeEnd) {
      const boundStart = (() => {
        let s = minFutureTime.startOf('minute');
        const a = utcHHMMToLocalDate(allowedTimeStart);
        if (a && s.isBefore(a)) s = a;
        return s;
      })();

      const boundEnd = (() => {
        const b = utcHHMMToLocalDate(allowedTimeEnd);
        if (b) return b;
        return dayjs(selectedDate).hour(23).minute(59).second(0)
          .millisecond(0);
      })();

      // Build occupied intervals for the day
      const intervals = slotsOnSameDay
        .map((s) => ({ start: dayjs(s.startTime), end: dayjs(s.endTime) }))
        .sort((a, b) => a.start.valueOf() - b.start.valueOf());

      // Iterate from boundStart in SLOT_GAP_MINUTES steps to find first non-overlapping slot
      let cursor = boundStart;
      const step = TIME_CONSTANTS.SLOT_GAP_MINUTES;
      while (cursor.add(duration, 'minute').valueOf() <= boundEnd.valueOf()) {
        const candidateEnd = cursor.add(duration, 'minute');
        // Overlap occurs when [cursor, candidateEnd) intersects [iv.start, iv.end)
        const currentCursor = cursor; // Capture for the closure
        const overlaps = intervals.some((iv) => candidateEnd.isAfter(iv.start) && currentCursor.isBefore(iv.end));
        if (!overlaps) {
          suggestedTime = cursor;
          break;
        }
        cursor = cursor.add(step, 'minute');
      }

      // Fallback to minFuture if nothing found
      if (!suggestedTime) suggestedTime = boundStart;
    } else if (slotsOnSameDay.length === 0) {
      // No existing slots: suggest 9:00 AM
      suggestedTime = dayjs(selectedDate).hour(TIME_CONSTANTS.DEFAULT_START_HOUR).minute(0).second(0)
        .millisecond(0);
    } else {
      // Find the latest end time and add configured gap
      const latestEndTime = slotsOnSameDay.reduce((latest, slot) => {
        const slotEndTime = dayjs(slot.endTime);
        return slotEndTime.isAfter(latest) ? slotEndTime : latest;
      }, dayjs(slotsOnSameDay[0].endTime));

      suggestedTime = latestEndTime.add(TIME_CONSTANTS.SLOT_GAP_MINUTES, 'minute');
    }

    // Ensure suggested time is at least 1 hour in the future.
    // startOf('hour') rounds down, so 22:06 becomes 22:00 and fails the cutoff.
    if (suggestedTime.isBefore(minFutureTime)) {
      const onTheHour = minFutureTime.startOf('hour');
      suggestedTime = onTheHour.isBefore(minFutureTime) ? onTheHour.add(1, 'hour') : onTheHour;
    }

    return suggestedTime.format('HH:mm');
  }, [selectedDate, existingSlots, viewMode, constraints]);

  const defaultTime = getDefaultTime();

  return {
    defaultTime,
    getDefaultTime,
  };
};
